import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { ProductCard } from "../components/ProductCard";
import { QuickView } from "../components/QuickView";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

const SORTS = [
  { value: "new", label: "Newest" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "name", label: "Name A–Z" },
];

export default function Shop() {
  const { categories } = useStore();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "new";
  const page = Number(params.get("page") || 1);
  const minPrice = params.get("min") || "";
  const maxPrice = params.get("max") || "";

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api.get("/products", {
      params: { q, category, sort, page, limit: 12, min_price: minPrice || 0, max_price: maxPrice || 0 },
    }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 1 }))
      .finally(() => setLoading(false));
  }, [q, category, sort, page, minPrice, maxPrice]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const filterPanel = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-medium tracking-[0.25em] uppercase">Categories</p>
        <ul className="mt-3 space-y-1.5 text-sm font-light">
          <li>
            <button data-testid="filter-cat-all" onClick={() => setParam("category", "")}
              className={`hover:text-primary ${!category ? "font-medium text-primary" : ""}`}>All products</button>
          </li>
          {categories.filter((c) => c.is_main).map((c) => (
            <li key={c.id}>
              <button data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.slug)}
                className={`hover:text-primary ${category === c.slug ? "font-medium text-primary" : ""}`}>{c.name}</button>
              {categories.some((s) => s.parent_id === c.id) && (
                <ul className="ml-4 mt-1 space-y-1 border-l border-input pl-3 text-xs">
                  {categories.filter((s) => s.parent_id === c.id).map((s) => (
                    <li key={s.id}>
                      <button data-testid={`filter-cat-${s.slug}`} onClick={() => setParam("category", s.slug)}
                        className={`hover:text-primary ${category === s.slug ? "font-medium text-primary" : ""}`}>{s.name}</button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {categories.filter((c) => !c.is_main && !c.parent_id).map((c) => (
            <li key={c.id}>
              <button data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.slug)}
                className={`text-muted-foreground hover:text-primary ${category === c.slug ? "font-medium text-primary" : ""}`}>{c.name}</button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs font-medium tracking-[0.25em] uppercase">Price</p>
        <div className="mt-3 flex items-center gap-2">
          <input data-testid="filter-min-price" type="number" min="0" placeholder="Min" value={minPrice}
            onChange={(e) => setParam("min", e.target.value)}
            className="w-full rounded-xl border border-input bg-white/70 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
          <span className="text-muted-foreground">–</span>
          <input data-testid="filter-max-price" type="number" min="0" placeholder="Max" value={maxPrice}
            onChange={(e) => setParam("max", e.target.value)}
            className="w-full rounded-xl border border-input bg-white/70 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="shop-page">
      <Seo title="Shop All — Anaya Fashion & Gifts" description="Browse the full Anaya boutique — vintage watches, jewellery, hampers, perfumes and accessories, hand-picked and hand-wrapped." path="/shop" />
      <Reveal>
        <p className="text-[11px] tracking-[0.4em] text-primary uppercase">The collection</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{q ? `Results for “${q}”` : "Shop all"}</h1>
        <p className="mt-2 text-sm font-light text-muted-foreground" data-testid="shop-total">{data.total} pieces</p>
      </Reveal>

      <div className="mt-8 flex gap-10">
        <aside className="hidden w-56 shrink-0 lg:block" data-testid="shop-filters">{filterPanel}</aside>
        <div className="flex-1">
          <div className="mb-6 flex items-center justify-between gap-3">
            <button data-testid="filters-toggle" onClick={() => setFiltersOpen(true)}
              className="flex items-center gap-2 rounded-full border border-input bg-white/70 px-4 py-2 text-xs tracking-widest uppercase lg:hidden">
              <SlidersHorizontal size={14} /> Filters
            </button>
            <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
              className="ml-auto rounded-full border border-input bg-white/70 px-4 py-2 text-xs tracking-widest uppercase outline-none">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4" data-testid="shop-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse"><div className="aspect-[3/4] rounded-[1.25rem] bg-secondary/60" /><div className="mt-3 h-4 w-3/4 rounded bg-secondary/60" /></div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="glass rounded-3xl py-20 text-center" data-testid="shop-empty">
              <p className="font-display text-2xl">Nothing here yet</p>
              <p className="mt-2 text-sm font-light text-muted-foreground">Try a different search or category — new pieces arrive weekly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4" data-testid="shop-grid">
              {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} onQuickView={setQuick} />)}
            </div>
          )}

          {data.pages > 1 && (
            <div className="mt-10 flex justify-center gap-2" data-testid="shop-pagination">
              {Array.from({ length: data.pages }).map((_, i) => (
                <button key={i} data-testid={`page-btn-${i + 1}`} onClick={() => setParam("page", String(i + 1))}
                  className={`h-9 w-9 rounded-full text-sm transition-colors ${page === i + 1 ? "bg-primary text-white" : "border border-input bg-white/70 hover:bg-secondary"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm lg:hidden" onClick={() => setFiltersOpen(false)} data-testid="filters-drawer">
          <div className="glass-deep h-full w-80 max-w-[85%] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <span className="font-display text-lg">Filters</span>
              <button onClick={() => setFiltersOpen(false)} data-testid="filters-close" aria-label="Close filters" className="rounded-full p-2 hover:bg-secondary"><X size={16} /></button>
            </div>
            {filterPanel}
          </div>
        </div>
      )}

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
